import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { financialReportsAPI, billsAPI, paymentsAPI } from '../services/api';
import { formatDate } from '../utils/helpers';
import { paymentStatusI18nKey } from '../utils/paymentStatusI18n';
import { startOfMonth } from 'date-fns';
import LoadingSpinner, { CardLoader } from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import Button from '../components/Button';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import FcfaIcon from '../components/FcfaIcon';

const SuperDashboardPage = () => {
  const { t } = useTranslation();
  useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [isRevenueChartModalOpen, setIsRevenueChartModalOpen] = useState(false);
  const [revenueChartZoom, setRevenueChartZoom] = useState(1);
  const getDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const defaultRangeStart = getDateInputValue(startOfMonth(new Date()));
  const defaultRangeEnd = getDateInputValue(new Date());
  const [timeframeDraft, setTimeframeDraft] = useState({ startDate: defaultRangeStart, endDate: defaultRangeEnd });
  const [timeframe, setTimeframe] = useState({ startDate: defaultRangeStart, endDate: defaultRangeEnd });
  const [timeframeError, setTimeframeError] = useState('');
  const reportParams = {
    period: selectedPeriod,
    startDate: timeframe.startDate,
    endDate: timeframe.endDate,
  };

  // API hooks for financial data
  const { data: overviewData, loading: overviewLoading } = useApi(
    () => financialReportsAPI.getOverview(reportParams), 
    [selectedPeriod, timeframe.startDate, timeframe.endDate], 
    { immediate: true }
  );

  const { data: trendsData, loading: trendsLoading } = useApi(
    () => financialReportsAPI.getRevenueTrends(reportParams), 
    [selectedPeriod, timeframe.startDate, timeframe.endDate], 
    { immediate: true }
  );

  const { data: clientPerformanceData } = useApi(
    () => financialReportsAPI.getClientPerformance(reportParams), 
    [selectedPeriod, timeframe.startDate, timeframe.endDate], 
    { immediate: true }
  );

  const { data: paymentMethodsData, loading: paymentMethodsLoading } = useApi(
    () => financialReportsAPI.getPaymentMethods(reportParams), 
    [selectedPeriod, timeframe.startDate, timeframe.endDate], 
    { immediate: true }
  );

  // Recent bills and payments data
  const { data: recentBillsData, loading: recentBillsLoading } = useApi(
    () => billsAPI.getRecent({ limit: 5 }), 
    [], 
    { immediate: true }
  );
  const { data: billsStatsData, loading: billsStatsLoading } = useApi(
    () => billsAPI.getStats({ startDate: timeframe.startDate, endDate: timeframe.endDate }),
    [timeframe.startDate, timeframe.endDate],
    { immediate: true }
  );

  const { data: recentPaymentsData, loading: recentPaymentsLoading } = useApi(
    () => paymentsAPI.getRecent({ limit: 5 }), 
    [], 
    { immediate: true }
  );

  // Infrastructure breakdown data
  const { data: infrastructureData, loading: infrastructureLoading } = useApi(
    () => financialReportsAPI.getInfrastructureBreakdown(reportParams), 
    [selectedPeriod, timeframe.startDate, timeframe.endDate], 
    { immediate: true }
  );

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => {
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500',
      indigo: 'bg-indigo-500',
      purple: 'bg-purple-500',
    };

    return (
      <div className="card">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`p-3 rounded-md ${colorClasses[color]}`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 leading-tight">
                {title}
              </dt>
              <dd className="text-lg font-medium text-gray-900">
                {value}
              </dd>
              {subtitle && (
                <dd className="text-xs text-gray-500">
                  {subtitle}
                </dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    );
  };

  const SimpleBarChart = ({ data, title, valueKey, labelKey }) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(...data.map(item => item[valueKey]));

    return (
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {data.map((item, index) => (
            <div key={index} className="flex items-center">
              <div className="w-20 text-sm text-gray-600 truncate">
                {item[labelKey]}
              </div>
              <div className="flex-1 ml-3">
                <div className="bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-blue-600 h-4 rounded-full transition-all duration-500"
                    style={{ width: `${(item[valueKey] / maxValue) * 100}%` }}
                  ></div>
                </div>
              </div>
              <div className="w-24 text-sm text-gray-900 text-right">
                {typeof item[valueKey] === 'number' && item[valueKey] > 1000 
                  ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(item[valueKey])
                  : item[valueKey]
                }
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const periods = [
    { value: 'daily', label: t('superDashboard.periods.daily') },
    { value: 'weekly', label: t('superDashboard.periods.weekly') },
    { value: 'monthly', label: t('superDashboard.periods.monthly') },
    { value: 'yearly', label: t('superDashboard.periods.yearly') }
  ];
  const formatXof = (value) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(Number(value || 0));

  const updateRevenueChartZoom = (delta) => {
    setRevenueChartZoom((prev) => {
      const next = Math.round((prev + delta) * 100) / 100;
      return Math.max(0.75, Math.min(2.5, next));
    });
  };
  const applyTimeframe = () => {
    const { startDate, endDate } = timeframeDraft;
    if (!startDate || !endDate) {
      setTimeframeError(t('superDashboard.timeframeRequired'));
      return;
    }
    if (startDate > endDate) {
      setTimeframeError(t('superDashboard.timeframeInvalid'));
      return;
    }
    setTimeframeError('');
    setTimeframe({ startDate, endDate });
  };
  const resetTimeframe = () => {
    const resetRange = { startDate: defaultRangeStart, endDate: defaultRangeEnd };
    setTimeframeError('');
    setTimeframeDraft(resetRange);
    setTimeframe(resetRange);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('superDashboard.title')}</h1>
            <p className="text-gray-600">{t('superDashboard.subtitle')}</p>
          </div>
        </div>

        {/* Period + Timeframe */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="block px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            title={t('superDashboard.grouping')}
          >
            {periods.map(period => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={timeframeDraft.startDate}
            max={timeframeDraft.endDate || undefined}
            onChange={(e) => setTimeframeDraft((prev) => ({ ...prev, startDate: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            title={t('superDashboard.startDate')}
          />
          <input
            type="date"
            value={timeframeDraft.endDate}
            min={timeframeDraft.startDate || undefined}
            onChange={(e) => setTimeframeDraft((prev) => ({ ...prev, endDate: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            title={t('superDashboard.endDate')}
          />
          <Button type="button" variant="outline" onClick={applyTimeframe}>
            {t('superDashboard.applyTimeframe')}
          </Button>
          <Button type="button" variant="ghost" onClick={resetTimeframe}>
            {t('superDashboard.resetTimeframe')}
          </Button>
        </div>
      </div>
      {timeframeError ? (
        <p className="text-sm text-red-600">{timeframeError}</p>
      ) : null}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {overviewLoading ? (
          <>
            <CardLoader />
            <CardLoader />
            <CardLoader />
            <CardLoader />
          </>
        ) : (
          <>
            <StatCard
              title={t('superDashboard.totalRevenue')}
              value={new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(overviewData?.overview?.totalRevenue || 0)}
              icon={FcfaIcon}
              color="purple"
              subtitle={`${overviewData?.overview?.paidBills || 0} ${t('superDashboard.billsPaid')}`}
            />
            <StatCard
              title={t('superDashboard.totalBills')}
              value={overviewData?.overview?.totalBills || 0}
              icon={CurrencyDollarIcon}
              color="blue"
              subtitle={`${overviewData?.overview?.pendingBills || 0} ${t('superDashboard.pending')}`}
            />
            <StatCard
              title={t('superDashboard.totalPayments')}
              value={overviewData?.overview?.totalPayments || 0}
              icon={CreditCardIcon}
              color="green"
              subtitle={`${overviewData?.overview?.completedPayments || 0} ${t('superDashboard.completed')}`}
            />
            <StatCard
              title={t('superDashboard.totalLoads')}
              value={overviewData?.overview?.totalLoads || 0}
              icon={TruckIcon}
              color="indigo"
              subtitle={`${((overviewData?.overview?.totalWeight || 0) / 1000).toFixed(1)}T ${t('superDashboard.totalWeight')}`}
            />
          </>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trends Chart */}
        <div className="card">
          <div className="mb-4 flex items-center justify-between gap-2">
            <h3 className="text-lg font-medium text-gray-900">{t('superDashboard.revenueTrends')}</h3>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsRevenueChartModalOpen(true)}
              >
                {t('superDashboard.expandChart')}
              </Button>
            </div>
          </div>
          {trendsLoading ? (
            <div className="h-64 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="h-64">
              <SimpleLineChart 
                data={trendsData?.trends || []} 
                valueKey="revenue" 
                labelKey="period"
                t={t}
                zoom={revenueChartZoom}
              />
            </div>
          )}
        </div>

        {/* Payment Methods Breakdown */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('superDashboard.paymentMethods')}</h3>
          {paymentMethodsLoading ? (
            <div className="h-64 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <SimplePieChart 
              data={paymentMethodsData?.paymentMethods || []} 
              valueKey="totalAmount" 
              labelKey="method"
              t={t}
            />
          )}
        </div>
      </div>

      {/* Outstanding Bills Amounts */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('superDashboard.outstandingBillsAmounts')}</h3>
        {billsStatsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <CardLoader />
            <CardLoader />
            <CardLoader />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">{t('superDashboard.totalBilledAmount')}</p>
              <p className="mt-2 text-xl font-semibold text-gray-900">{formatXof(billsStatsData?.stats?.totalAmount)}</p>
              <p className="mt-1 text-xs text-gray-500">
                {(billsStatsData?.stats?.totalBills || 0)} {t('superDashboard.bills')}
              </p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs uppercase tracking-wide text-amber-700">{t('superDashboard.outstandingAmount')}</p>
              <p className="mt-2 text-xl font-semibold text-amber-900">{formatXof(billsStatsData?.stats?.outstandingAmount)}</p>
              <p className="mt-1 text-xs text-amber-700">
                {t('superDashboard.pendingAndOverdueCount', {
                  pending: billsStatsData?.stats?.pendingBills || 0,
                  overdue: billsStatsData?.stats?.overdueBills || 0
                })}
              </p>
            </div>
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-xs uppercase tracking-wide text-yellow-700">{t('superDashboard.pendingAmount')}</p>
              <p className="mt-2 text-xl font-semibold text-yellow-900">{formatXof(billsStatsData?.stats?.pendingAmount)}</p>
              <p className="mt-1 text-xs text-yellow-700">
                {(billsStatsData?.stats?.pendingBills || 0)} {t('superDashboard.pending')}
              </p>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-xs uppercase tracking-wide text-red-700">{t('superDashboard.overdueAmount')}</p>
              <p className="mt-2 text-xl font-semibold text-red-900">{formatXof(billsStatsData?.stats?.overdueAmount)}</p>
              <p className="mt-1 text-xs text-red-700">
                {(billsStatsData?.stats?.overdueBills || 0)} {t('bills.overdue')}
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-xs uppercase tracking-wide text-green-700">{t('superDashboard.paidAmount')}</p>
              <p className="mt-2 text-xl font-semibold text-green-900">{formatXof(billsStatsData?.stats?.paidAmount)}</p>
              <p className="mt-1 text-xs text-green-700">
                {(billsStatsData?.stats?.paidBills || 0)} {t('bills.paid')}
              </p>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-xs uppercase tracking-wide text-blue-700">{t('superDashboard.pendingVerificationAmount')}</p>
              <p className="mt-2 text-xl font-semibold text-blue-900">{formatXof(billsStatsData?.stats?.waitingVerificationAmount)}</p>
              <p className="mt-1 text-xs text-blue-700">
                {(billsStatsData?.stats?.billsWaitingApproval || 0)} {t('superDashboard.waitingVerification')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Client Performance */}
      <SimpleBarChart
        data={clientPerformanceData?.clientPerformance?.slice(0, 10) || []}
        title={t('superDashboard.topClients')}
        valueKey="totalRevenue"
        labelKey="name"
      />

      {/* Infrastructure Performance */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('superDashboard.infrastructureBreakdown')}</h3>
        {infrastructureLoading ? (
          <div className="space-y-3">
            <CardLoader />
            <CardLoader />
            <CardLoader />
          </div>
        ) : infrastructureData?.infrastructureBreakdown?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('superDashboard.infrastructure')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('superDashboard.type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('superDashboard.revenue')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('superDashboard.bills')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('superDashboard.clients')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {infrastructureData.infrastructureBreakdown.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.type === 'weighbridge' ? 'bg-purple-100 text-purple-800' :
                        item.type === 'mole' ? 'bg-blue-100 text-blue-800' :
                        item.type === 'port' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {t(`superDashboard.types.${item.type}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(item.totalRevenue)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.billCount}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.clientCount}</div>
                      <div className="text-xs text-gray-500">
                        {item.clients.slice(0, 2).join(', ')}
                        {item.clients.length > 2 && ` +${item.clients.length - 2} more`}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <BuildingOfficeIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>{t('superDashboard.noInfrastructureData')}</p>
          </div>
        )}
      </div>

      {/* Financial Summary Table */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('superDashboard.financialSummary')}</h3>
        {overviewLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(overviewData?.overview?.totalRevenue || 0)}
              </div>
              <div className="text-sm text-gray-500">{t('superDashboard.revenue')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {overviewData?.overview?.totalBills || 0}
              </div>
              <div className="text-sm text-gray-500">{t('superDashboard.bills')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {overviewData?.overview?.totalPayments || 0}
              </div>
              <div className="text-sm text-gray-500">{t('superDashboard.payments')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {overviewData?.overview?.totalLoads || 0}
              </div>
              <div className="text-sm text-gray-500">{t('superDashboard.loads')}</div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Bills and Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Bills */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.recentBills')}</h3>
            <Link
              to="/bills"
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              {t('dashboard.viewAll')}
            </Link>
          </div>
          
          {recentBillsLoading ? (
            <div className="space-y-3">
              <CardLoader />
              <CardLoader />
              <CardLoader />
            </div>
          ) : recentBillsData?.recentBills?.length > 0 ? (
            <div className="space-y-3">
              {recentBillsData.recentBills.map((bill) => (
                <Link
                  key={bill.id}
                  to="/bills"
                  state={{ openBillId: bill.id }}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left no-underline text-inherit"
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="p-2 bg-green-100 rounded">
                      <FcfaIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {bill.billNumber}
                        </p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                          bill.status === 'PAID' ? 'bg-green-100 text-green-800' :
                          bill.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          bill.status === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {t(`bills.${bill.status?.toLowerCase()}`)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {bill.certificate?.client?.companyName} • {bill.certificate?.product?.name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDate(bill.generatedDate)}
                        {bill.payments?.[0] && (
                          <span className={`ml-2 inline-flex px-1 py-0.5 text-xs font-medium rounded ${
                            bill.payments[0].status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                            bill.payments[0].status === 'WAITING_FOR_VERIFICATION' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {t(paymentStatusI18nKey(bill.payments[0], bill.payments), {
                              defaultValue: bill.payments[0].status,
                            })}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(bill.totalAmount)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <FcfaIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>{t('dashboard.noRecentBills')}</p>
            </div>
          )}
        </div>

        {/* Recent Payments */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('dashboard.recentPayments')}</h3>
            <Link
              to="/payments"
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              {t('dashboard.viewAll')}
            </Link>
          </div>
          
          {recentPaymentsLoading ? (
            <div className="space-y-3">
              <CardLoader />
              <CardLoader />
              <CardLoader />
            </div>
          ) : recentPaymentsData?.recentPayments?.length > 0 ? (
            <div className="space-y-3">
              {recentPaymentsData.recentPayments.map((payment) => (
                <Link
                  key={payment.id}
                  to="/payments"
                  state={{ openPaymentId: payment.id }}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left no-underline text-inherit"
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="p-2 bg-blue-100 rounded">
                      <CreditCardIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {payment.bill?.billNumber}
                        </p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                          payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          payment.status === 'WAITING_FOR_VERIFICATION' ? 'bg-orange-100 text-orange-800' :
                          payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          payment.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {t(paymentStatusI18nKey(payment), { defaultValue: payment.status })}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {payment.bill?.certificate?.client?.companyName} • {payment.paymentMethod}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDate(payment.processedDate)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(payment.amount)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <CreditCardIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>{t('dashboard.noRecentPayments')}</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isRevenueChartModalOpen}
        onClose={() => setIsRevenueChartModalOpen(false)}
        title={t('superDashboard.revenueTrends')}
        size="xl"
      >
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600">{t('superDashboard.chartZoomHint')}</p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => updateRevenueChartZoom(-0.25)}
                disabled={revenueChartZoom <= 0.75}
                title={t('superDashboard.zoomOut')}
              >
                {t('superDashboard.zoomOutShort')}
              </Button>
              <span className="min-w-[60px] text-center text-xs font-medium text-gray-600">
                {Math.round(revenueChartZoom * 100)}%
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => updateRevenueChartZoom(0.25)}
                disabled={revenueChartZoom >= 2.5}
                title={t('superDashboard.zoomIn')}
              >
                {t('superDashboard.zoomInShort')}
              </Button>
            </div>
          </div>
          <div className="h-[70vh]">
            <SimpleLineChart
              data={trendsData?.trends || []}
              valueKey="revenue"
              labelKey="period"
              t={t}
              zoom={revenueChartZoom}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Simple Line Chart Component
const SimpleLineChart = ({ data, valueKey, labelKey, t, zoom = 1 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <ChartBarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>{t('messages.noDataFound')}</p>
        </div>
      </div>
    );
  }

  const values = data.map((item) => Number(item[valueKey] || 0));
  const maxValueRaw = Math.max(...values);
  const minValueRaw = Math.min(...values);
  const spread = Math.max(1, maxValueRaw - minValueRaw);
  const yPadding = spread * 0.15;
  const minValue = Math.max(0, minValueRaw - yPadding);
  const maxValue = maxValueRaw + yPadding;
  const safeRange = Math.max(1, maxValue - minValue);
  const pointStepX = data.length > 1 ? 700 / (data.length - 1) : 0;
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  const formatCompactXof = (value) =>
    new Intl.NumberFormat('fr-FR', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(Math.round(value));

  const formatFullXof = (value) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      maximumFractionDigits: 0,
    }).format(Math.round(value));

  const pointY = (value) => 160 - ((Number(value || 0) - minValue) / safeRange) * 120;
  const points = data.map((item, index) => {
    const x = 50 + index * pointStepX;
    const y = pointY(item[valueKey]);
    return { x, y, raw: Number(item[valueKey] || 0), label: item[labelKey] };
  });

  return (
    <div className="h-full p-4">
      <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
        <span>{t('superDashboard.revenue')}</span>
        <span className="font-medium text-gray-700">
          Max: {formatFullXof(maxValueRaw)}
        </span>
      </div>
      <div className="h-[calc(100%-24px)] overflow-auto">
      <svg
        width={`${Math.max(100, Math.round(zoom * 100))}%`}
        height="100%"
        viewBox="0 0 820 220"
        className="overflow-visible min-w-[760px]"
      >
        {/* Grid lines */}
        {yTicks.map((tick) => {
          const y = 160 - tick * 120;
          const tickValue = minValue + tick * safeRange;
          return (
            <g key={tick}>
              <line
                x1="50"
                y1={y}
                x2="750"
                y2={y}
                stroke="#E5E7EB"
                strokeWidth="1"
              />
              <text
                x="44"
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill="#6B7280"
              >
                {formatCompactXof(tickValue)}
              </text>
            </g>
          );
        })}
        
        {/* Data line */}
        <polyline
          fill="none"
          stroke="#7C3AED"
          strokeWidth="3"
          points={points.map((p) => `${p.x},${p.y}`).join(' ')}
        />
        
        {/* Data points */}
        {points.map((point, index) => {
          return (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r="4"
                fill="#7C3AED"
              >
                <title>{`${point.label}: ${formatFullXof(point.raw)}`}</title>
              </circle>
              <text
                x={point.x}
                y={point.y - 10}
                textAnchor="middle"
                fontSize="10"
                fill="#4C1D95"
                className="font-medium"
              >
                {formatCompactXof(point.raw)}
              </text>
            </g>
          );
        })}
        
        {/* Labels */}
        {data.map((item, index) => {
          const x = 50 + index * pointStepX;
          return (
            <text
              key={index}
              x={x}
              y="188"
              textAnchor="middle"
              fontSize="12"
              fill="#6B7280"
            >
              {item[labelKey]}
            </text>
          );
        })}
      </svg>
      </div>
    </div>
  );
};

// Simple Pie Chart Component
const SimplePieChart = ({ data, valueKey, labelKey, t }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <ChartBarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>{t('messages.noDataFound')}</p>
        </div>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item[valueKey], 0);
  const colors = ['#7C3AED', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
  
  let currentAngle = 0;

  return (
    <div className="h-64 flex items-center">
      <div className="w-1/2">
        <svg width="200" height="200" viewBox="0 0 200 200">
          {data.map((item, index) => {
            const percentage = (item[valueKey] / total) * 100;
            const angle = (percentage / 100) * 360;
            const startAngle = currentAngle;
            const endAngle = currentAngle + angle;
            
            currentAngle += angle;

            const startAngleRad = (startAngle - 90) * (Math.PI / 180);
            const endAngleRad = (endAngle - 90) * (Math.PI / 180);

            const largeArcFlag = angle > 180 ? 1 : 0;

            const x1 = 100 + 80 * Math.cos(startAngleRad);
            const y1 = 100 + 80 * Math.sin(startAngleRad);
            const x2 = 100 + 80 * Math.cos(endAngleRad);
            const y2 = 100 + 80 * Math.sin(endAngleRad);

            const pathData = [
              `M 100 100`,
              `L ${x1} ${y1}`,
              `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
              `Z`
            ].join(' ');

            return (
              <path
                key={index}
                d={pathData}
                fill={colors[index % colors.length]}
                stroke="white"
                strokeWidth="2"
              />
            );
          })}
        </svg>
      </div>
      
      <div className="w-1/2 space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center">
            <div
              className="w-4 h-4 rounded mr-2"
              style={{ backgroundColor: colors[index % colors.length] }}
            ></div>
            <div className="text-sm">
              <div className="font-medium">{t(`payments.methods.${item[labelKey].toLowerCase().replace('_', '')}`)}</div>
              <div className="text-gray-500">
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(item[valueKey])}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuperDashboardPage;
